<template>
  <div>
    <ClinicPageHeader
      title="Financeiro da Clínica"
      subtitle="Operação financeira — faturamento, recebimentos e repasses aos profissionais de saúde"
    >
      <template #actions>
        <select
          v-model="month"
          class="select"
          data-test="month"
          style="width:180px;height:40px"
          @change="onMonthChange"
        >
          <option v-for="m in months" :key="m.value" :value="m.value">
            {{ m.label }}
          </option>
        </select>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <div class="tabs" style="margin-bottom:var(--space-6)">
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceDashboard', query: monthQuery }"
        >
          Visão geral
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceReceivables', query: monthQuery }"
        >
          Recebimentos
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceTransfers', query: monthQuery }"
        >
          Repasses
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact-path
          :to="{ name: 'ClinicFinanceStatement', query: monthQuery }"
        >
          Extrato
        </router-link>
      </div>

      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar os repasses"
        message="Ocorreu um erro ao buscar os repasses da clínica. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="fetch">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <!-- 403: sem acesso ao Financeiro nesta clínica (US-C11) -->
      <ClinicStateBox
        v-else-if="state === 'forbidden'"
        state="empty"
        title="Acesso restrito"
        message="Você não tem acesso ao Financeiro desta clínica. Fale com o administrador."
      />

      <template v-else-if="state === 'loading'">
        <div class="metrics3" style="margin-bottom:var(--space-5)">
          <div v-for="i in 3" :key="i" class="metric">
            <div class="skel-line" style="width:70%;margin-bottom:8px" />
            <div class="skel-line" style="width:45%;height:24px" />
          </div>
        </div>
        <div class="card">
          <div class="skel-line" style="width:100%;height:200px" />
        </div>
      </template>

      <ClinicStateBox
        v-else-if="!rows.length"
        state="empty"
        title="Nenhum repasse neste mês"
        message="Não há consultas pagas da clínica no mês selecionado."
      />

      <!-- Conteúdo -->
      <template v-else>
        <div class="metrics3" style="margin-bottom:var(--space-5)">
          <div class="metric">
            <span class="metric-label">A repassar</span>
            <span class="metric-value sm">{{ kpiMoney(toTransferCents) }}</span>
            <span class="metric-foot">
              {{ pendingCount }} profissionais de saúde
            </span>
          </div>
          <div class="metric">
            <span class="metric-label">Repassado</span>
            <span class="metric-value sm money--pos">
              {{ kpiMoney(transferredCents) }}
            </span>
            <span class="metric-foot">
              {{ paidCount }} profissionais de saúde
            </span>
          </div>
          <div class="metric">
            <span class="metric-label">Janela de pagamento</span>
            <span class="metric-value sm">{{ windowLabel }}</span>
            <span class="metric-foot">de cada mês, pela Videomedi</span>
          </div>
        </div>

        <div
          v-if="payoutWindow"
          class="card"
          style="background:var(--color-primary-light);border-color:var(--color-primary);display:flex;align-items:center;gap:var(--space-4);margin-bottom:var(--space-5)"
        >
          <div
            class="metric-icon"
            style="background:#fff;color:var(--color-primary)"
            v-html="icons.clock"
          />
          <div
            style="flex:1;font-size:var(--font-size-sm);color:var(--color-gray-700)"
          >
            Os repasses são pagos pela Videomedi entre os dias
            {{ payoutWindow.startDay }} e {{ payoutWindow.endDay }} de cada mês.
            A clínica acompanha aqui o status de cada profissional de saúde.
          </div>
        </div>

        <div class="card card--flush">
          <div class="card-head flush">
            <h3>Repasses por profissional de saúde</h3>
          </div>
          <table class="table" style="margin-top:var(--space-2)">
            <thead>
              <tr>
                <th>Profissional de saúde</th>
                <th>Consultas</th>
                <th>A repassar</th>
                <th>Status</th>
                <th>Previsão</th>
                <th class="t-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.id"
                class="tr--link"
                @click="goDoctor(row.doctorId)"
              >
                <td>
                  <div class="row" style="gap:var(--space-3)">
                    <div
                      class="avatar avatar--sm"
                      :class="(row.doctor || {}).avatarClass"
                    >
                      {{ (row.doctor || {}).initials }}
                    </div>
                    <div class="stack" style="gap:0">
                      <span class="cell-strong">
                        {{ (row.doctor || {}).name }}
                      </span>
                      <span
                        style="font-size:var(--font-size-xs);color:var(--color-gray-400)"
                      >
                        {{ (row.doctor || {}).specialty }}
                      </span>
                    </div>
                  </div>
                </td>
                <td class="num">{{ row.appointments }}</td>
                <td class="num money">{{ formatMoney(row.amountCents) }}</td>
                <td>
                  <span
                    class="badge"
                    :class="`badge--${statusInfo(row.status).variant}`"
                  >
                    {{ statusInfo(row.status).label }}
                  </span>
                </td>
                <td class="num">{{ formatDate(row.expectedDate) }}</td>
                <td class="t-right">
                  <button
                    class="btn btn--outline btn--sm"
                    @click.stop="goDoctor(row.doctorId)"
                  >
                    Ver detalhe
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import { ClinicFinance } from "@/services/clinica";
import { formatMoney } from "@/data/clinica";
import clinicFinanceMonth from "@/mixins/clinicFinanceMonth";

const ICONS = {
  clock: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/><path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`
};

// Repasse: status do backend → rótulo + variante do badge.
const STATUS = {
  previsto: { label: "Previsto", variant: "warning" },
  em_processamento: { label: "Em processamento", variant: "info" },
  pago: { label: "Pago", variant: "success" }
};

export default {
  name: "ClinicFinanceTransfers",
  components: { ClinicPageHeader, ClinicStateBox },
  mixins: [clinicFinanceMonth],
  data() {
    return {
      // loading → ready/error. Preview de erro via ?state=error.
      state: "loading",
      icons: ICONS,
      payoutWindow: null,
      // Repasses por médico (cada linha já traz .doctor do service).
      rows: []
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    formatMoney,
    kpiMoney(cents) {
      return ((cents || 0) / 100).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    onMonthChanged() {
      this.fetch();
    },
    async fetch() {
      this.state = "loading";
      try {
        const { data } = await ClinicFinance.transfers({ month: this.month });
        this.rows = data;
        this.state = "ready";
        this.fetchWindow();
      } catch (e) {
        // 403 = RBAC (papel Atendimento) — não é falha de carregamento
        this.state =
          e && e.response && e.response.status === 403 ? "forbidden" : "error";
      }
    },
    statusInfo(status) {
      return STATUS[status] || { label: status, variant: "neutral" };
    },
    // Janela de pagamento (Setting do painel interno), vinda do summary.
    async fetchWindow() {
      if (this.payoutWindow) return;
      try {
        const { data } = await ClinicFinance.summary();
        this.payoutWindow = data.payoutWindow;
      } catch (e) {
        // acessório: sem a janela, a faixa informativa só não aparece
      }
    },
    formatDate(iso) {
      if (!iso) return "—";
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    },
    goDoctor(id) {
      this.$router.push({
        name: "ClinicDoctorProfile",
        params: { id: String(id) }
      });
    }
  },
  computed: {
    // A repassar = ainda não pago (previsto + em processamento).
    pendingRows() {
      return this.rows.filter(r => r.status !== "pago");
    },
    pendingCount() {
      return this.pendingRows.length;
    },
    paidCount() {
      return this.rows.filter(r => r.paidCents > 0).length;
    },
    toTransferCents() {
      return this.rows.reduce(
        (sum, r) => sum + (r.pendingCents || 0) + (r.processingCents || 0),
        0
      );
    },
    transferredCents() {
      return this.rows.reduce((sum, r) => sum + (r.paidCents || 0), 0);
    },
    windowLabel() {
      return this.payoutWindow
        ? `Dias ${this.payoutWindow.startDay} a ${this.payoutWindow.endDay}`
        : "—";
    }
  }
};
</script>
